import { ChevronRight, Home } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  className?: string;
  showHome?: boolean;
}

// Route label mappings for auto-generated breadcrumbs
const routeLabels: Record<string, string> = {
  "": "Home",
  user: "Account",
  account: "Account Info",
  update: "Contact Info",
  publicprofile: "Public Profile",
  "public-profile": "Public Profile",
  changepassword: "Change Password",
  "change-password": "Change Password",
  "confirm-email": "Confirm Email",
  confirmEmail: "Confirm Email",
  newsletter: "Newsletter Opt-in",
  brands: "Brands",
  breweries: "My Breweries",
  updatereview: "Brands",
  orders: "Order History",
  invoices: "Invoices",
  invoice: "Pay Invoice",
  completed: "Invoice Completed",
  "press-releases": "Press Releases",
  upgrade: "Upgrade",
  success: "Success",
  EditBrand: "Edit Brand",
  EditProducts: "Edit Products",
  CreateProduct: "Create Product",
  CreateSubBrand: "Create Sub-Brand",
  editProductInfo: "Edit Product Info",
  UploadProductPhotos: "Upload Product Photos",
  EditProductDetails: "Edit Product Details",
  create: "Create",
  edit: "Edit",
  upload: "Upload",
  help: "Help",
};

// Brand action segments that appear between /updatereview/ and the brand guid
const brandActionSegments = new Set([
  'EditBrand', 'CreateProduct', 'CreateSubBrand', 'EditProducts',
  'editProductInfo', 'UploadProductPhotos', 'EditProductDetails',
]);

function generateBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const segments = pathname.split("/").filter(Boolean);
  const breadcrumbs: BreadcrumbItem[] = [];

  if (segments[0] === 'breweries') {
    breadcrumbs.push({ label: 'My Breweries', href: segments.length > 1 ? '/breweries' : undefined });
    if (segments.length > 1) {
      const guid = segments[1];
      breadcrumbs.push({ label: window.__BREWERY_NAMES__?.[guid] || 'Brewery', href: `/breweries/${encodeURIComponent(guid)}/edit/general` });
      if (segments[2] === 'beers') breadcrumbs.push({ label: 'Beers', href: `/breweries/${encodeURIComponent(guid)}/edit/beers` }, { label: segments[3] === 'new' ? 'Add beer' : 'Edit beer' });
      else { const labels: Record<string, string> = { general: 'General', addresses: 'Addresses', social: 'Social', overview: 'Overview', logo: 'Logo', beers: 'Beers', production: 'Annual Production', jobs: 'Jobs' }; breadcrumbs.push({ label: labels[segments[3]] || 'General' }); }
    }
    return breadcrumbs;
  }
  // Brand routes (/updatereview/...) need special handling:
  // the brand guid appears after the action segment, but breadcrumbs
  // should show Brands > BrandName > Action
  if (segments[0] === 'updatereview') {
    breadcrumbs.push({ label: 'Brands', href: '/brands' });

    if (segments.length >= 2) {
      const isId = /^[0-9]+$/.test(segments[1]) || /^[a-f0-9-]{32,36}$/i.test(segments[1]);

      if (isId) {
        // /updatereview/{brandGuid} — Brand Options page
        const brandName = window.__BRAND_NAMES__?.[segments[1]];
        breadcrumbs.push({ label: brandName || '...' });
      } else if (brandActionSegments.has(segments[1]) && segments.length >= 3) {
        // /updatereview/{Action}/{brandGuid}[/{productGuid}]
        const brandGuid = segments[2];
        const brandName = window.__BRAND_NAMES__?.[brandGuid];
        breadcrumbs.push({ label: brandName || '...', href: `/updatereview/${brandGuid}` });
        const actionLabel = routeLabels[segments[1]] || segments[1];
        breadcrumbs.push({ label: actionLabel });
      }
    }

    return breadcrumbs;
  }

  // NOSH brand routes (/nosh/brands/{brandGuid}[/...]): Brands > BrandName > page
  if (segments[0] === 'nosh' && segments[1] === 'brands' && segments.length >= 3) {
    const [, , brandGuid, page, sub] = segments;
    const hub = `/nosh/brands/${brandGuid}`;
    breadcrumbs.push({ label: 'Brands', href: '/brands' });
    breadcrumbs.push({ label: window.__BRAND_NAMES__?.[brandGuid] || '...', href: page ? hub : undefined });

    if (page === 'edit') {
      breadcrumbs.push({ label: 'Brand Details' });
    } else if (page === 'where-to-buy') {
      breadcrumbs.push({ label: 'Where to Buy' });
    } else if (page === 'subbrands') {
      breadcrumbs.push({ label: 'Add Sub-Brand' });
    } else if (page === 'products') {
      breadcrumbs.push({ label: 'Products', href: sub ? `${hub}/products` : undefined });
      if (sub) {
        breadcrumbs.push({ label: sub === 'new' ? 'Add Product' : 'Edit Product' });
      }
    }

    return breadcrumbs;
  }

  // Press releases upgrade routes (/user/press-releases/upgrade/{guid}[/success])
  // need special handling because /user/press-releases/upgrade doesn't exist without the guid
  if (segments.length >= 4 && segments[0] === 'user' && segments[1] === 'press-releases' && segments[2] === 'upgrade') {
    const guid = segments[3];
    breadcrumbs.push({ label: 'Account', href: '/user' });
    breadcrumbs.push({ label: 'Press Releases', href: '/user/press-releases' });
    if (segments[4] === 'success') {
      breadcrumbs.push({ label: 'Upgrade', href: `/user/press-releases/upgrade/${guid}` });
      breadcrumbs.push({ label: 'Success' });
    } else {
      breadcrumbs.push({ label: 'Upgrade' });
    }
    return breadcrumbs;
  }

  let currentPath = "";
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const isLast = index === segments.length - 1;

    // Check if this segment is an ID (numeric or UUID-like, with or without dashes)
    const isId = /^[0-9]+$/.test(segment) || /^[a-f0-9-]{32,36}$/i.test(segment);

    if (isId) {
      // For non-brand IDs (like job applicants), skip them entirely in the breadcrumb
      // This prevents showing GUIDs or "..." for job listings, applicants, etc.
    } else {
      const label =
        routeLabels[segment] ||
        segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, " ");

      breadcrumbs.push({
        label,
        href: isLast ? undefined : currentPath,
      });
    }
  });

  return breadcrumbs;
}

export function Breadcrumbs({
  items,
  className,
  showHome = true,
}: BreadcrumbsProps) {
  const location = useLocation();
  const [, forceUpdate] = useState({});

  // Listen for brand name registration events to update breadcrumbs
  useEffect(() => {
    const handleBrandNameRegistered = () => {
      forceUpdate({});
    };

    window.addEventListener('brandNameRegistered', handleBrandNameRegistered);
    window.addEventListener('breweryNameRegistered', handleBrandNameRegistered);
    return () => {
      window.removeEventListener('brandNameRegistered', handleBrandNameRegistered);
      window.removeEventListener('breweryNameRegistered', handleBrandNameRegistered);
    };
  }, []);

  const breadcrumbItems = items || generateBreadcrumbs(location.pathname);

  // Don't render if only home breadcrumb
  if (breadcrumbItems.length === 0) {
    return null;
  }

  return (
    <nav aria-label="Breadcrumb" className={cn("flex items-center", className)}>
      <ol className="flex items-center gap-1.5 text-body-sm">
        {showHome && (
          <>
            <li>
              <Link
                to="/user/account"
                className={cn(
                  "flex items-center text-muted-foreground",
                  "hover:text-foreground transition-colors duration-150",
                  "focus-visible:outline-none focus-visible:ring-2",
                  "focus-visible:ring-ring focus-visible:ring-offset-2",
                  "rounded-sm"
                )}
                aria-label="Home"
              >
                <Home className="h-4 w-4" />
              </Link>
            </li>
            {breadcrumbItems.length > 0 && (
              <li aria-hidden="true">
                <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
              </li>
            )}
          </>
        )}

        {breadcrumbItems.map((item, index) => (
          <li key={index} className="flex items-center gap-1.5">
            {index > 0 && (
              <ChevronRight
                className="h-4 w-4 text-muted-foreground/50"
                aria-hidden="true"
              />
            )}
            {item.href ? (
              <Link
                to={item.href}
                className={cn(
                  "text-muted-foreground hover:text-foreground",
                  "transition-colors duration-150",
                  "focus-visible:outline-none focus-visible:ring-2",
                  "focus-visible:ring-ring focus-visible:ring-offset-2",
                  "rounded-sm px-0.5"
                )}
              >
                {item.label}
              </Link>
            ) : (
              <span className="font-medium text-foreground" aria-current="page">
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
